import pluralize from 'pluralize';
import { z } from 'zod';
import { DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE, isDocumentDiscountWithinBounds, } from './document-discount.js';
import { optionalStr } from './utils.js';
/** Default billing unit suggestions surfaced in the UI combobox. */
export const UNIT_TYPE_DEFAULTS = [
    'HOUR',
    'DAY',
    'WEEK',
    'MONTH',
    'UNIT',
    'FLAT',
];
/* Quantity-stepper increment per unit type. Time-based units (hour, day) step in quarter
   increments to support partial-time billing; count-based units (week, month, unit, flat)
   step by whole numbers. Custom unit types not listed here fall back to
   {@link DEFAULT_QUANTITY_STEP}. */
export const UNIT_TYPE_QUANTITY_STEP = {
    HOUR: 0.25,
    DAY: 0.25,
    WEEK: 1,
    MONTH: 1,
    UNIT: 1,
    FLAT: 1,
};
/* Fallback quantity-stepper increment for unit types not in
   {@link UNIT_TYPE_QUANTITY_STEP}. */
export const DEFAULT_QUANTITY_STEP = 1;
/** Resolves the quantity-stepper increment for a given (possibly custom) unit type. */
export function getQuantityStepForUnitType(unitType) {
    const normalized = unitType.trim().toUpperCase();
    /* Custom unit types aren't in DefaultUnitType; the `?? DEFAULT_QUANTITY_STEP` below
       handles a lookup miss for those, so the cast here is safe. */
    return UNIT_TYPE_QUANTITY_STEP[normalized] ?? DEFAULT_QUANTITY_STEP;
}
// Unit types that describe a lump sum rather than a countable quantity.
const NON_PLURALIZING_UNIT_TYPES = new Set(['FLAT']);
/**
 * Formats a (possibly custom) unit type for human display, pluralizing when quantity is
 * not exactly 1. Custom unit types are pluralized with {@link pluralize} since
 * {@link LineItemUnitTypeSchema} allows arbitrary free text.
 */
export function formatUnitTypeLabel(unitType, quantity) {
    const label = unitType.trim().toLowerCase();
    if (NON_PLURALIZING_UNIT_TYPES.has(unitType.trim().toUpperCase()))
        return label;
    return quantity === 1 ? label : pluralize(label);
}
/**
 * Billing unit for a line item — any non-empty string up to 50 chars, normalised to
 * uppercase.
 */
export const LineItemUnitTypeSchema = z
    .string()
    .min(1, { error: 'Unit type is required' })
    .max(50, { error: 'Unit type must be 50 characters or fewer' })
    .transform((s) => s.trim().toUpperCase())
    .pipe(z.string().min(1, { error: 'Unit type is required' }));
/** How the discount value is applied to a line item. */
export const LineItemDiscountTypeSchema = z.enum(['NONE', 'PERCENTAGE', 'FIXED']);
/**
 * Base field shape for a standalone catalogue line item, unwrapped from the
 * {@link isDocumentDiscountWithinBounds} refinement so consumers needing `.shape` or
 * `.pick()` (unavailable on a refined `ZodEffects`) can still derive from it — see
 * {@link CreateLineItemSchema} for the validated version.
 */
export const LineItemFieldsSchema = z.object({
    title: z.string().min(1, { error: 'Title must contain at least 1 character' }).max(255),
    description: optionalStr,
    unitPrice: z.coerce
        .number({ error: 'Unit price must be a number' })
        .min(0, { error: 'Unit price must be greater than or equal to 0' }),
    unitType: LineItemUnitTypeSchema.default('HOUR'),
    quantity: z.coerce.number().positive({ error: 'Quantity must be greater than 0' }),
    discount: z.coerce.number().min(0).default(0),
    discountType: LineItemDiscountTypeSchema.default('NONE'),
    taxable: z.boolean().default(true),
});
/** Request body for creating a standalone catalogue line item. */
export const CreateLineItemSchema = LineItemFieldsSchema.refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Partial version of {@link CreateLineItemSchema} for PATCH updates. */
export const UpdateLineItemSchema = LineItemFieldsSchema.partial().refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/**
 * Computes the line item subtotal after applying the discount.
 *
 * @param unitPrice - Price per unit before discount.
 * @param quantity - Number of units.
 * @param discount - Discount amount or percentage value.
 * @param discountType - How the discount is applied: PERCENTAGE, FIXED, or NONE.
 * @returns The subtotal after discount, floored at 0.
 */
export function computeLineItemSubtotal(unitPrice, quantity, discount, discountType) {
    const gross = unitPrice * quantity;
    if (discountType === 'PERCENTAGE')
        return gross * (1 - discount / 100);
    if (discountType === 'FIXED')
        return Math.max(0, gross - discount);
    return gross;
}
//# sourceMappingURL=line-items.js.map